import * as React from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Chip,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';

import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import BrushIcon from '@mui/icons-material/Brush';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HistoryIcon from '@mui/icons-material/History';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import { TokenValidator } from '../../utils';
import { Settings } from '../../controllers';
import LabelDesigner from './designer';
import { ZPL_FIELDS } from './zplDesigner';

// Editor de plantillas ZPL con diseñador visual — port de la pestaña Plantillas
// del configurador de boletas de ARMENTO, adaptado al almacenamiento de Carlos
// (archivos en zplTemplates + versiones automáticas en _versions/).
export default function TemplatesEditor({ overview, reloadOverview }) {

    const token = async() => await TokenValidator.GetInternalUser();

    const zplRef = React.useRef(null);

    const [selected, setSelected] = React.useState('');
    const [name, setName] = React.useState('');
    const [zpl, setZpl] = React.useState('');
    const [loadedFrom, setLoadedFrom] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [report, setReport] = React.useState(null);
    const [previewUrl, setPreviewUrl] = React.useState('');
    const [message, setMessage] = React.useState(null);
    const [designerOpen, setDesignerOpen] = React.useState(false);
    const [versionsUi, setVersionsUi] = React.useState(false);
    const [versions, setVersions] = React.useState([]);

    const templates = overview.templates || [];

    const templateUsage = (fileName) => {
        const rows = (overview.zpls || []).filter(x => x.zpl === fileName && x.active);
        const keys = new Set(rows.map(x => `${x.client.replace(/\s+/g, '')}/${x.product}`));
        return keys.size;
    };

    const setPreviewBlob = (blob) => {
        setPreviewUrl(prev => {
            if (prev){ setTimeout(() => URL.revokeObjectURL(prev), 1500); }
            return blob ? URL.createObjectURL(blob) : '';
        });
    };

    // guarda de concurrencia: si el usuario da clic rápido entre plantillas,
    // solo la ÚLTIMA carga puede escribir el estado del editor
    const loadSeqRef = React.useRef(0);

    const loadTemplate = async(fileName) => {
        const mySeq = ++loadSeqRef.current;
        setBusy(true);
        const response = await Settings.GetZplTemplateContent(await token(), fileName);
        if(mySeq !== loadSeqRef.current){ return; }
        setBusy(false);

        if(response === undefined || !response.success){
            setMessage({ severity: 'error', text: 'No se pudo leer la plantilla' });
            return;
        }

        setSelected(fileName);
        setName(fileName);
        setZpl(response.result.content);
        setLoadedFrom(fileName);
        setReport(null);
        setPreviewBlob(null);
        setMessage(null);
        doPreview(response.result.content);
    };

    const newTemplate = () => {
        setSelected('');
        setName('');
        setZpl('^XA\n^PW812\n^LL406\n\n^XZ\n');
        setLoadedFrom('');
        setReport(null);
        setPreviewBlob(null);
        setMessage(null);
    };

    const duplicate = () => {
        if(!zpl.trim()){ return; }
        const proposed = (name || 'plantilla.zpl').replace(/\.zpl$/i, '') + '_copia.zpl';
        setSelected('');
        setName(proposed);
        setLoadedFrom('');
        setMessage({ severity: 'info', text: `Editando una copia — se guardará como archivo nuevo (${proposed})` });
    };

    const insertField = (field) => {
        const el = zplRef.current;
        if(!el){ setZpl(zpl + field); return; }
        const start = el.selectionStart || 0;
        const end = el.selectionEnd || start;
        const next = zpl.slice(0, start) + field + zpl.slice(end);
        setZpl(next);
        setTimeout(() => { el.focus(); el.setSelectionRange(start + field.length, start + field.length); }, 0);
    };

    const doValidate = async() => {
        setBusy(true);
        const response = await Settings.ValidateZpl(await token(), zpl);
        setBusy(false);

        if(response === undefined || !response.success){
            setMessage({ severity: 'error', text: 'Error al validar (renderer no disponible)' });
            return null;
        }
        setReport(response.result);
        return response.result;
    };

    const doPreview = async(source = null) => {
        setBusy(true);
        const r = await Settings.RenderZplPreview(await token(), source !== null ? source : zpl);
        setBusy(false);

        if(!r || !r.blob){
            setMessage({ severity: 'error', text: `Error al renderizar: ${(r && r.error) || 'renderer no disponible'}` });
            return;
        }
        setMessage(null);
        setPreviewBlob(r.blob);
    };

    const doSave = async() => {
        let fileName = String(name || '').trim();
        if(!fileName){
            setMessage({ severity: 'error', text: 'Pon un nombre de archivo (ej. clientenuevo.zpl)' });
            return;
        }
        if(!fileName.toLowerCase().endsWith('.zpl')){ fileName += '.zpl'; }

        const rep = await doValidate();
        if(rep && rep.ok === false){
            setMessage({ severity: 'error', text: 'No se guardó: corrige los errores de validación' });
            return;
        }

        setBusy(true);
        const response = await Settings.SaveTemplateContent(await token(), fileName, zpl);
        setBusy(false);

        if(response === undefined || !response.success){
            setMessage({ severity: 'error', text: response === undefined ? 'Error al guardar' : response.message });
            return;
        }

        setSelected(fileName);
        setName(fileName);
        setLoadedFrom(fileName);
        setMessage({ severity: 'success', text: response.message });
        await reloadOverview();
    };

    const openVersions = async() => {
        if(!loadedFrom){ return; }
        setBusy(true);
        const response = await Settings.GetTemplateVersions(await token(), loadedFrom);
        setBusy(false);

        if(response === undefined || !response.success){
            setMessage({ severity: 'error', text: 'No se pudieron listar las versiones' });
            return;
        }
        setVersions(response.result);
        setVersionsUi(true);
    };

    const viewVersion = async(file) => {
        const response = await Settings.GetVersionContent(await token(), file);
        if(response === undefined || !response.success){ return; }
        setZpl(response.result.content);
        setVersionsUi(false);
        setMessage({ severity: 'info', text: `Viendo la versión ${file} — guarda para convertirla en la versión actual` });
        doPreview(response.result.content);
    };

    const restoreVersion = async(file) => {
        if(!window.confirm(`¿Restaurar ${loadedFrom} desde ${file}? La versión actual también queda respaldada.`)){ return; }
        setBusy(true);
        const response = await Settings.RestoreTemplateVersion(await token(), loadedFrom, file);
        setBusy(false);

        if(response === undefined || !response.success){
            setMessage({ severity: 'error', text: 'Error al restaurar' });
            return;
        }
        setVersionsUi(false);
        setMessage({ severity: 'success', text: response.message });
        await loadTemplate(loadedFrom);
        await reloadOverview();
    };

    const handleUpload = async(e, filesOverride = null, overwrite = false) => {
        const files = filesOverride || Array.from((e && e.target && e.target.files) || []);
        if(e && e.target){ e.target.value = null; }
        if(files.length < 1){ return; }

        setBusy(true);
        const response = await Settings.UploadZplTemplate(await token(), files, overwrite);
        setBusy(false);

        if(response === undefined){
            setMessage({ severity: 'error', text: 'Error al subir plantillas' });
            return;
        }

        const failed = (response.result && response.result.failed) || [];
        const existing = failed.filter(x => String(x.reason || '').includes('already exists'));

        if(existing.length > 0 && !overwrite){
            const names = existing.map(x => x.name);
            if(window.confirm(`Ya existen: ${names.join(', ')}.\n¿Sobrescribir? (la versión anterior se conserva en el historial)`)){
                const retry = files.filter(f => names.includes(String(f.name || '').replaceAll(' ', '')));
                await handleUpload(null, retry, true);
                return;
            }
        }

        if(!response.success || failed.length > 0){
            setMessage({ severity: response.success ? 'warning' : 'error',
                         text: failed.length > 0
                            ? `Algunos archivos fallaron: ${failed.map(x => `${x.name} (${x.reason})`).join(', ')}`
                            : (response.message || 'Error al subir plantillas') });
        }else{
            setMessage({ severity: 'success', text: response.message });
        }
        await reloadOverview();
    };

    // API que consume el diseñador
    const apiPreview = async(zplText, dataOrNull) => await Settings.RenderZplPreview(await token(), zplText, dataOrNull);
    const apiImageToZpl = async(payload) => {
        const response = await Settings.ImageToZpl(await token(), payload);
        return (response && response.success) ? response.result : null;
    };

    return (
        <Box sx={{ padding: 2, width: '100%', display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            {/* lista de plantillas */}
            <Paper sx={{ width: 270, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', paddingX: 1.5, paddingTop: 1 }}>
                    <Typography variant='subtitle1' sx={{ flexGrow: 1 }}>Plantillas</Typography>
                    <Tooltip title='Subir archivos .zpl'>
                        <IconButton size='small' component='label' disabled={busy}>
                            <UploadFileIcon fontSize='small' />
                            <input type='file' hidden multiple accept='.zpl' onChange={handleUpload} />
                        </IconButton>
                    </Tooltip>
                    <IconButton size='small' onClick={reloadOverview} disabled={busy}><RefreshIcon fontSize='small' /></IconButton>
                </Box>
                <Box sx={{ paddingX: 1.5, paddingBottom: 1 }}>
                    <Button fullWidth size='small' variant='outlined' startIcon={<AddIcon />} onClick={newTemplate}>
                        Nueva plantilla
                    </Button>
                </Box>
                <List dense sx={{ maxHeight: '60vh', overflow: 'auto' }}>
                    {templates.map(t => (
                        <ListItemButton key={t.name} selected={selected === t.name} disabled={busy}
                            onClick={() => loadTemplate(t.name)}>
                            <ListItemText
                                primary={t.name}
                                secondary={`${t.bytes} bytes — usada por ${templateUsage(t.name)} cliente/producto`}
                                primaryTypographyProps={{ fontSize: 13, fontFamily: 'monospace' }}
                                secondaryTypographyProps={{ fontSize: 11 }}
                            />
                        </ListItemButton>
                    ))}
                </List>
            </Paper>

            {/* editor */}
            <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField label='Archivo' size='small' sx={{ width: 260 }} value={name}
                        placeholder='clientenuevo.zpl'
                        onChange={(e) => setName(e.target.value)} />
                    <Button variant='contained' size='small' startIcon={<BrushIcon />} disabled={busy}
                        onClick={() => setDesignerOpen(true)}>
                        Diseñar
                    </Button>
                    <Button size='small' startIcon={<VisibilityIcon />} disabled={busy} onClick={() => doPreview()}>
                        Vista previa
                    </Button>
                    <Button size='small' startIcon={<CheckCircleOutlineIcon />} disabled={busy} onClick={doValidate}>
                        Validar
                    </Button>
                    <Button variant='contained' color='success' size='small' startIcon={<SaveIcon />} disabled={busy}
                        onClick={doSave}>
                        Guardar
                    </Button>
                    <Button size='small' startIcon={<ContentCopyIcon />} disabled={busy || !zpl.trim()} onClick={duplicate}>
                        Duplicar
                    </Button>
                    <Button size='small' startIcon={<HistoryIcon />} disabled={busy || !loadedFrom} onClick={openVersions}>
                        Versiones
                    </Button>
                    {busy && <CircularProgress size={20} />}
                </Box>

                {message && <Alert severity={message.severity} onClose={() => setMessage(null)}>{message.text}</Alert>}

                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {ZPL_FIELDS.map(f => (
                        <Chip key={f} label={f} size='small' variant='outlined' onClick={() => insertField(f)}
                            sx={{ fontFamily: 'monospace', fontSize: 11 }} />
                    ))}
                </Box>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <TextField
                        multiline
                        minRows={18}
                        maxRows={28}
                        sx={{ flexGrow: 1, minWidth: 380, '& textarea': { fontFamily: 'monospace', fontSize: 12.5, whiteSpace: 'pre' } }}
                        value={zpl}
                        inputRef={zplRef}
                        onChange={(e) => setZpl(e.target.value)}
                        placeholder='^XA ... ^XZ'
                    />
                    <Box sx={{ width: 380, flexShrink: 0 }}>
                        {previewUrl ? (
                            <Paper variant='outlined' sx={{ padding: 1 }}>
                                <img src={previewUrl} alt='vista previa' style={{ maxWidth: '100%', display: 'block' }} />
                            </Paper>
                        ) : (
                            <Typography variant='body2' sx={{ color: 'gray' }}>
                                La vista previa (render real con datos de muestra) aparece aquí.
                            </Typography>
                        )}
                        {report && (
                            <Alert severity={report.ok ? 'success' : 'error'} sx={{ marginTop: 1 }}>
                                {report.ok ? 'ZPL válido.' : `Errores: ${(report.errors || []).join('; ')}`}
                                {(report.warnings || []).length > 0 && ` Avisos: ${report.warnings.join('; ')}`}
                                {(report.fields_used || []).length > 0 && ` Campos: ${report.fields_used.join(', ')}`}
                            </Alert>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* diseñador visual */}
            <LabelDesigner
                open={designerOpen}
                name={name || '(sin nombre)'}
                zpl={zpl}
                onClose={() => setDesignerOpen(false)}
                onApply={(finalZpl) => {
                    setZpl(finalZpl);
                    setDesignerOpen(false);
                    doPreview(finalZpl);
                    setMessage({ severity: 'info', text: 'Diseño aplicado al código — recuerda Guardar' });
                }}
                apiPreview={apiPreview}
                apiImageToZpl={apiImageToZpl}
            />

            {/* versiones */}
            <Dialog open={versionsUi} onClose={() => setVersionsUi(false)} maxWidth='sm' fullWidth>
                <DialogTitle>Versiones de {loadedFrom}</DialogTitle>
                <DialogContent>
                    {versions.length < 1 && (
                        <Typography variant='body2' sx={{ color: 'gray' }}>
                            Sin versiones anteriores (se crean automáticamente en cada guardado).
                        </Typography>
                    )}
                    {versions.map(v => (
                        <Box key={v.file} sx={{ display: 'flex', alignItems: 'center', gap: 1, paddingY: 0.5,
                                                borderBottom: '1px solid #eee' }}>
                            <Typography variant='body2' sx={{ fontFamily: 'monospace', flexGrow: 1, fontSize: 12 }}>
                                {v.stamp} ({v.bytes} bytes)
                            </Typography>
                            <Button size='small' onClick={() => viewVersion(v.file)}>Ver</Button>
                            <Button size='small' color='warning' onClick={() => restoreVersion(v.file)}>Restaurar</Button>
                        </Box>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setVersionsUi(false)}>Cerrar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
